// ============================================================================
// project-requesters.ts: a job's requesters, matched to the people in Clients,
// and the email recipients they give (Peter, 2026-10-13; branch
// claude/crm-requesters-email).
// ----------------------------------------------------------------------------
// "It is ideal for a Requester [to] have an email and live inside the Clients
// (Firm/Company) section as one of the people ... when a Quote is generated,
// and the user wants to 'EMAIL', then it should email to the generic email
// address for the Client, and also send to the requesting Strata Manager's
// email address."
//
// Pure: no React, no network. The server answer is GET
// /api/projects/:id/requesters (backend/src/services/project-requesters.js).
//
// Decisions (orchestrator, binding; E1 as Peter refined it on 2026-10-13):
//   E1  "The Quote needs to be emailed to the Firm's ... email address where
//       quotes are requested to be sent to, as well as to the 'Order By'
//       person ... even if their job title isn't Strata Manager."
//       To = (1) the firm's quotes address: the address the RFQ says quotes go
//       to (formData.quoteReturnEmail), else the firm's general email in
//       Clients; then (2) the Order By person: the RFQ requester with role
//       'order_by', else the first requester, whatever their title, with the
//       RFQ's email for them, else their Clients email. Cc stays the business
//       CC. A strata manager or assistant on the RFQ who is not the Order By
//       person is offered as "Also on this request" (never pre-filled). Each
//       address says where it came from; an Order By person with no email
//       anywhere is named in a warning.
//   E2  The RFQ's email for a person wins for this job when Clients holds a
//       different one; Clients is only updated when the operator asks.
//   E3  A job whose RFQ names nobody uses its linked requester, else the one
//       linked on the last job of the same building.
// Nothing here reads a file name: only the RFQ text and Clients decide.
// ============================================================================
import { REQUESTER_SOURCE_LABELS, rfqFirmHints, type RfqFirmHints, type RfqRequesterRole } from './rfq-requesters';

export interface ClientsPerson {
    id: string;
    companyId: string | null;
    companyName: string | null;
    firstName: string;
    lastName: string;
    name: string;
    title: string | null;
    email: string | null;
    phone: string | null;
    mobile: string | null;
    isPrimary: boolean;
    viaBuilding: boolean;
    active: boolean;
}

export type RequesterLinkRole = 'primary' | 'cc';

export interface ProjectRequestersAnswer {
    projectId: string;
    company: { id: string; name: string | null; companyType: string | null; generalEmail: string | null } | null;
    people: ClientsPerson[];
    linked: Array<{ contactId: string; role: RequesterLinkRole; person: ClientsPerson }>;
    inherited: {
        projectId: string;
        title: string | null;
        quoteNumber: string | null;
        matchedBy: 'building' | 'strata-plan';
        plan: string | null;
        links: Array<{ contactId: string; role: RequesterLinkRole }>;
    } | null;
}

/** A requester as the job (the RFQ) names them. */
export interface JobRequester {
    name: string;
    title: string | null;
    email: string | null;
    phone: string | null;
    role: RfqRequesterRole | string;
}

export interface RecipientLine {
    email: string;
    /** Where the address came from, in the operator's words. */
    source: string;
    contactId: string | null;
}

export interface UpdateClientsOffer {
    contactId: string;
    name: string;
    clientsEmail: string;
    rfqEmail: string;
}

/** A strata manager or assistant on the request who is not the Order By person. */
export interface AlsoOnRequest {
    name: string;
    title: string | null;
    /** null when neither the RFQ nor Clients has one: shown, nothing to add. */
    email: string | null;
    source: string | null;
    contactId: string | null;
}

export interface ClientsRecipients {
    to: RecipientLine[];
    /** Always empty from Clients: Cc stays the business CC (E1). */
    cc: RecipientLine[];
    /** Offered under the fields with Add to To and Add to CC (D5), never pre-filled (E1). */
    alsoOnRequest: AlsoOnRequest[];
    warnings: string[];
    updateOffers: UpdateClientsOffer[];
}

const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const email = (value: unknown): string => text(value).toLowerCase();
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
/** The fingerprint shape the server accepts (migration 137 CHECK). */
export const FORM_FINGERPRINT_RE = /^[A-Za-z0-9:._-]{4,200}$/;

/** A person's name as matched: lower case, letters and digits, single spaces. */
export const personNameKey = (value: unknown): string => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

const ROLE_TITLES: Record<string, string> = {
    strata_manager: 'Strata Manager',
    assistant: 'Assistant Strata Manager',
};

/** The title to file a requester under: theirs, else the one their RFQ role implies. */
export function requesterTitle(requester: Pick<JobRequester, 'title' | 'role'>): string | null {
    return text(requester.title) || ROLE_TITLES[String(requester.role || '')] || null;
}

/**
 * A Site Contact on the RFQ (a body corporate or building manager, a tenant or
 * an owner): contacted only to book the site visit (Peter, 2026-10-13). Never a
 * requester, never an email recipient, never one of the firm's people.
 */
export interface SiteContact {
    name: string;
    email: string | null;
    phone: string | null;
    /**
     * They are also a requester (an owner who asked for the quote and lets the builder in): their name or email is
     * the main requester's, or a vouched requester's (isVouchedRequester). Their address is not a site contact's only,
     * and their name does not make a requester a site contact (reviews R2-8, R3-5, R3-6, R4-4, R4-5). The server
     * applies the same rule (backend/src/services/project-requesters.js replaceProjectRequesters).
     */
    alsoRequester?: boolean;
}

/**
 * A requester entry the request or the operator has named as a requester: the
 * Order By person, the main requester (marked by Stage 1), one the operator
 * has handled as a requester (made main before, or added by hand), or one the
 * request prints under a requester label. Never a site contact, even when they
 * also let the builder in. An entry the AI alone named is not vouched: it is
 * still checked against the site contacts.
 */
export function isVouchedRequester(entry: { role?: unknown; main?: unknown; confirmed?: unknown; source?: unknown } | null | undefined): boolean {
    const role = String(entry?.role || '');
    if (role === 'site_contact') return false;
    const label = entry?.source && typeof entry.source === 'object' ? String((entry.source as { label?: unknown }).label || '') : '';
    return role === 'order_by' || entry?.main === true || entry?.confirmed === true || REQUESTER_SOURCE_LABELS.has(label);
}

/** formData.siteContacts[] (from the RFQ extraction) and the single siteContact* fields. */
export function readSiteContacts(formData: Record<string, any> | null | undefined): SiteContact[] {
    const fd = formData || {};
    const out: SiteContact[] = [];
    const seen = new Set<string>();
    const vouched = Array.isArray(fd.requesters) ? fd.requesters.filter((entry: any) => entry && typeof entry === 'object' && isVouchedRequester(entry)) : [];
    const requesterNames = new Set([personNameKey(fd.clientName), ...vouched.map((entry: any) => personNameKey(entry.name))].filter(Boolean));
    const requesterEmails = new Set([email(fd.clientEmail), ...vouched.map((entry: any) => email(entry.email))].filter(Boolean));
    const push = (entry: any) => {
        const name = text(entry?.name ?? entry?.fullName);
        const address = email(entry?.email);
        const phone = text(entry?.phone ?? entry?.mobile) || null;
        if (!name && !address && !phone) return;
        const key = `${personNameKey(name)}|${address}|${phone || ''}`;
        if (seen.has(key)) return;
        seen.add(key);
        const valid = EMAIL_RE.test(address) ? address : null;
        const alsoRequester = (!!valid && requesterEmails.has(valid)) || requesterNames.has(personNameKey(name));
        out.push({ name, email: valid, phone, ...(alsoRequester ? { alsoRequester: true } : {}) });
    };
    if (Array.isArray(fd.siteContacts)) for (const entry of fd.siteContacts) push(entry);
    push({ name: fd.siteContactName, email: fd.siteContactEmail, phone: fd.siteContactPhone });
    return out;
}

/** The addresses that belong to site contacts only, lower case: never a requester's, never an email recipient. */
export function siteContactEmails(siteContacts: SiteContact[]): Set<string> {
    return new Set(siteContacts.filter(contact => !contact.alsoRequester).map(contact => contact.email).filter((value): value is string => !!value));
}

/**
 * Whether a requester entry is really a site contact: its RFQ role says so,
 * its email is a site contact's only, or (unless the RFQ names them as the
 * Order By person) its full name is a site contact's only. The Order By
 * exception is deliberate: an email import copies the requester's name into
 * siteContactName (Stage1DataEntry.tsx, the emailExtract autofill), and that
 * must not drop the person who asked for the quote. A site contact who is
 * also a requester (alsoRequester: a vouched requester's name or
 * email) makes nobody a site contact.
 */
export function isSiteContactEntry(entry: { name?: unknown; email?: unknown; role?: unknown }, siteContacts: SiteContact[]): boolean {
    if (String(entry?.role || '') === 'site_contact') return true;
    const address = email(entry?.email);
    if (address && siteContactEmails(siteContacts).has(address)) return true;
    if (String(entry?.role || '') === 'order_by') return false;
    const key = personNameKey(entry?.name);
    return key.split(' ').length >= 2 && siteContacts.some(contact => !contact.alsoRequester && personNameKey(contact.name) === key);
}

/**
 * The job's requesters: formData.requesters when the RFQ extraction filled it,
 * else the single requester in clientName / clientEmail / clientTitle. A Site
 * Contact is never a requester.
 */
export function readJobRequesters(formData: Record<string, any> | null | undefined): JobRequester[] {
    const fd = formData || {};
    const siteContacts = readSiteContacts(fd);
    const out: JobRequester[] = [];
    const seen = new Set<string>();
    const push = (entry: any, fallbackRole: string) => {
        const name = text(entry?.name);
        const address = email(entry?.email);
        if (!name && !address) return;
        if (isSiteContactEntry({ name, email: address, role: text(entry?.role) || fallbackRole }, siteContacts)) return;
        const key = `${personNameKey(name)}|${address}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({
            name,
            title: text(entry?.title) || null,
            email: EMAIL_RE.test(address) ? address : null,
            phone: text(entry?.phone) || null,
            role: text(entry?.role) || fallbackRole,
        });
    };
    if (Array.isArray(fd.requesters) && fd.requesters.length) {
        for (const entry of fd.requesters) push(entry, 'contact');
    } else {
        push({ name: fd.clientName, title: fd.clientTitle, email: fd.clientEmail, phone: fd.clientPhone }, 'order_by');
    }
    return out;
}

/**
 * The person in Clients a requester is: the same email, else the one person of
 * the firm with exactly that full name. null when neither.
 */
export function matchRequester(requester: Pick<JobRequester, 'name' | 'email'>, people: ClientsPerson[]): { person: ClientsPerson; by: 'email' | 'name' } | null {
    const address = email(requester.email);
    if (address) {
        const byEmail = people.find(person => email(person.email) === address);
        if (byEmail) return { person: byEmail, by: 'email' };
    }
    const key = personNameKey(requester.name);
    if (key.split(' ').length >= 2) {
        const byName = people.filter(person => personNameKey(person.name || `${person.firstName} ${person.lastName}`) === key);
        if (byName.length === 1 && byName[0]) return { person: byName[0], by: 'name' };
    }
    return null;
}

/** First name and the rest, for a new contact. */
export function splitPersonName(name: string): { firstName: string; lastName: string } {
    const parts = text(name).split(/\s+/).filter(Boolean);
    return { firstName: parts[0] || '', lastName: parts.slice(1).join(' ') };
}

const personLabel = (person: { name?: string | null; title?: string | null }, fallbackTitle?: string | null) => {
    const name = text(person.name) || 'A requester';
    const title = text(person.title) || text(fallbackTitle);
    return title ? `${name}, ${title}` : name;
};

/** The RFQ roles a strata manager or assistant has: offered as "Also on this request". */
const MANAGER_ROLES = new Set(['strata_manager', 'assistant']);

/** The Order By person on the RFQ: the 'order_by' requester, else the first one. */
export function orderByRequester(jobRequesters: JobRequester[]): JobRequester | null {
    return jobRequesters.find(requester => requester.role === 'order_by') || jobRequesters[0] || null;
}

/**
 * E1 (Peter's refinement) + E2 + E3. To is, in this order: (1) the firm's
 * quotes address (the RFQ's `quoteReturnEmail`, else the firm's general email
 * in Clients) and (2) the Order By person (the RFQ's address for them, else
 * their Clients email). Cc is left to the business CC. Strata managers and
 * assistants on the RFQ who are not the Order By person, and the job's other
 * linked requesters, are returned in `alsoOnRequest`, never in To. Each address
 * carries its source; an Order By person with no email anywhere is a warning;
 * an RFQ address that differs from Clients is an update offer (E2).
 */
export function buildClientsRecipients(
    answer: ProjectRequestersAnswer | null | undefined,
    jobRequesters: JobRequester[],
    options: { quoteReturnEmail?: unknown; siteContacts?: SiteContact[] } = {},
): ClientsRecipients {
    const result: ClientsRecipients = { to: [], cc: [], alsoOnRequest: [], warnings: [], updateOffers: [] };
    if (!answer) return result;
    // A Site Contact is only for the site visit: never To, never offered.
    const siteEmails = siteContactEmails(options.siteContacts || []);
    const people = Array.isArray(answer.people) ? answer.people : [];
    const byId = new Map(people.map(person => [person.id, person]));
    const linked = Array.isArray(answer.linked) ? answer.linked.filter(link => link.person?.active !== false) : [];
    const used = new Set<string>();
    const add = (address: string, source: string, contactId: string | null) => {
        const key = email(address);
        if (!EMAIL_RE.test(key) || used.has(key) || siteEmails.has(key)) return;
        used.add(key);
        result.to.push({ email: key, source, contactId });
    };
    const offer = (person: ClientsPerson | null, name: string, rfqEmail: string) => {
        const clientsEmail = email(person?.email);
        if (person && rfqEmail && clientsEmail && clientsEmail !== rfqEmail
            && !result.updateOffers.some(existing => existing.contactId === person.id)) {
            result.updateOffers.push({ contactId: person.id, name: name || clientsEmail, clientsEmail, rfqEmail });
        }
    };

    // (1) The firm's quotes address.
    const quotesAddress = email(options.quoteReturnEmail);
    const general = email(answer.company?.generalEmail);
    if (EMAIL_RE.test(quotesAddress) && !siteEmails.has(quotesAddress)) add(quotesAddress, 'From the RFQ: where quotes are to be sent', null);
    else if (general) add(general, `Clients: ${answer.company?.name || 'the client'} general`, null);

    // (2) The Order By person, whatever their title. A job whose RFQ names
    // nobody uses its linked requester, else the last job's (E3).
    const orderByRfq = orderByRequester(jobRequesters);
    let orderBy: { person: ClientsPerson | null; rfq: JobRequester | null } | null = null;
    if (orderByRfq) {
        orderBy = { person: matchRequester(orderByRfq, people)?.person || null, rfq: orderByRfq };
    } else {
        const link = linked.find(entry => entry.role !== 'cc') || linked[0];
        const inheritedLink = linked.length ? null
            : (answer.inherited?.links || []).find(entry => entry.role !== 'cc') || answer.inherited?.links?.[0] || null;
        const person = link?.person || (inheritedLink ? byId.get(inheritedLink.contactId) || null : null);
        if (person) orderBy = { person, rfq: null };
    }
    if (orderBy) {
        const name = orderBy.rfq?.name || orderBy.person?.name || '';
        const title = orderBy.person?.title || (orderBy.rfq ? requesterTitle(orderBy.rfq) : null);
        const rfqEmail = email(orderBy.rfq?.email);
        const clientsEmail = email(orderBy.person?.email);
        if (rfqEmail) {
            add(rfqEmail, `From the RFQ: ${name || 'the Order By person'} (Order By)`, orderBy.person?.id || null);
            offer(orderBy.person, name, rfqEmail);
        } else if (clientsEmail) {
            add(clientsEmail, `Clients: ${personLabel({ name, title })} (Order By)`, orderBy.person?.id || null);
        } else {
            result.warnings.push(`No email for ${name || 'the Order By person'} (Order By) in the RFQ or in Clients. Add one in Clients.`);
        }
    }

    // Also on this request: never pre-filled, one click adds them.
    const alsoIds = new Set<string>(orderBy?.person ? [orderBy.person.id] : []);
    const pushAlso = (person: ClientsPerson | null, rfq: JobRequester | null) => {
        if (person && alsoIds.has(person.id)) return;
        if (person) alsoIds.add(person.id);
        const name = rfq?.name || person?.name || '';
        if (!name) return;
        const title = person?.title || (rfq ? requesterTitle(rfq) : null);
        const rfqEmail = email(rfq?.email);
        const clientsEmail = email(person?.email);
        const address = rfqEmail || clientsEmail;
        if (siteEmails.has(rfqEmail) || siteEmails.has(clientsEmail)) return;
        result.alsoOnRequest.push({
            name,
            title,
            email: EMAIL_RE.test(address) ? address : null,
            source: rfqEmail ? 'From the RFQ' : clientsEmail ? `Clients: ${personLabel({ name, title })}` : null,
            contactId: person?.id || null,
        });
        if (rfqEmail) offer(person, name, rfqEmail);
    };
    for (const requester of jobRequesters) {
        if (requester === orderByRfq || !MANAGER_ROLES.has(String(requester.role))) continue;
        pushAlso(matchRequester(requester, people)?.person || null, requester);
    }
    for (const link of linked) pushAlso(link.person, null);
    return result;
}

/**
 * What the RFQ's TEXT says about its firm (E5 signals 2 and 5). The extraction
 * branch may keep the hints on formData.rfqFirmHints; otherwise they are read
 * from the RFQ text. A file name is never read.
 */
export function jobFirmHints(formData: Record<string, any> | null | undefined): RfqFirmHints {
    const kept = formData?.rfqFirmHints;
    const hints: RfqFirmHints = kept && typeof kept === 'object'
        ? {
            firmNames: Array.isArray(kept.firmNames) ? kept.firmNames : [],
            abns: Array.isArray(kept.abns) ? kept.abns : [],
            emailDomains: Array.isArray(kept.emailDomains) ? kept.emailDomains : [],
            formFingerprint: typeof kept.formFingerprint === 'string' ? kept.formFingerprint : null,
        }
        : rfqFirmHints(typeof formData?.rfqContent === 'string' ? formData.rfqContent : '');
    const strings = (list: unknown[]) => [...new Set(list.map(text).filter(Boolean))].slice(0, 10);
    const print = text(hints.formFingerprint);
    return {
        firmNames: strings(hints.firmNames).filter(name => name.length <= 255),
        abns: strings(hints.abns).map(abn => abn.replace(/\D/g, '')).filter(abn => abn.length === 11),
        emailDomains: strings(hints.emailDomains),
        formFingerprint: FORM_FINGERPRINT_RE.test(print) ? print : null,
    };
}
